import React, { useState } from 'react';
import { useRooms } from '../hooks/useRooms';

interface CreateRoomModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateRoomModal: React.FC<CreateRoomModalProps> = ({ isOpen, onClose }) => {
  const [roomName, setRoomName] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { createRoom } = useRooms();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!roomName.trim()) {
      setError('Por favor ingresa un nombre para la sala');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Establecer un tiempo límite para la creación de la sala
      const timeoutPromise = new Promise<null>((_, reject) => {
        setTimeout(() => reject(new Error('Tiempo de espera agotado al crear la sala')), 15000);
      });

      // Crear la sala en Supabase con tiempo límite
      const room = await Promise.race([
        createRoom(roomName, isPublic),
        timeoutPromise
      ]);

      // Verificar que la sala se creó correctamente
      if (!room || !room.id) {
        throw new Error('No se pudo crear la sala, respuesta incompleta del servidor');
      }

      console.log(`Sala creada exitosamente: ${room.id}`);

      // Guardar los datos de la sala en localStorage para recuperarlos en la página Room
      localStorage.setItem(`room_data_${room.id}`, JSON.stringify(room));

      // Cerrar el modal
      onClose();

      // En lugar de usar setRedirectTo, redirigir directamente usando window.location
      const roomUrl = `/room/${room.id}?t=${Date.now()}`;
      console.log(`Redirigiendo a: ${roomUrl}`);

      // Pequeño retraso para asegurar que el estado se actualice antes de la redirección
      setTimeout(() => {
        window.location.href = roomUrl;
      }, 100);

    } catch (err: any) {
      console.error('Error al crear la sala:', err);
      setError(err.message || 'Error al crear la sala. Inténtalo de nuevo.');

      // Si el error es de tiempo de espera, sugerir al usuario que refresque la página
      if (err.message && err.message.includes('Tiempo de espera agotado')) {
        setError('La operación está tardando demasiado. Intenta refrescar la página y vuelve a intentarlo.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/70"></div>
        </div>

        <div
          className="inline-block align-bottom rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          style={{ backgroundColor: 'var(--card-bg)' }}
        >
          <div className="p-6 sm:p-8">
            <div className="flex justify-between items-center mb-4">
              <h3
                className="text-xl font-semibold"
                style={{ color: 'var(--text-color)' }}
              >
                Crear nueva sala
              </h3>
              <button
                onClick={onClose}
                className="rounded-full p-1 hover:bg-gray-500/10 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  style={{ color: 'var(--text-secondary)' }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {error && (
              <div
                className="mb-4 p-3 rounded-lg text-sm"
                style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', color: 'rgb(239, 68, 68)' }}
              >
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label
                  htmlFor="roomName"
                  className="block mb-2 text-sm font-medium"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Nombre de la sala
                </label>
                <input
                  id="roomName"
                  type="text"
                  value={roomName}
                  onChange={(e) => setRoomName(e.target.value)}
                  placeholder="Ej. Noche de películas con amigos"
                  className="themed-input w-full rounded-xl px-4 py-3 text-sm"
                  disabled={loading}
                />
              </div>

              <div className="mb-6">
                <label className="flex items-center cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={isPublic}
                      onChange={() => setIsPublic(!isPublic)}
                      disabled={loading}
                    />
                    <div
                      className="block w-14 h-8 rounded-full transition-colors"
                      style={{ backgroundColor: isPublic ? 'var(--accent-color)' : 'var(--border-color)' }}
                    ></div>
                    <div
                      className={`absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${
                        isPublic ? 'transform translate-x-6' : ''
                      }`}
                    ></div>
                  </div>
                  <div className="ml-3">
                    <span
                      className="text-sm font-medium"
                      style={{ color: 'var(--text-color)' }}
                    >
                      Sala pública
                    </span>
                    <p
                      className="text-xs"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {isPublic
                        ? 'Tu sala será visible para todos los usuarios'
                        : 'Solo podrán unirse usuarios con el enlace o código'}
                    </p>
                  </div>
                </label>
              </div>

              <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded-xl border text-sm font-medium transition-colors"
                  style={{
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-secondary)'
                  }}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="themed-button px-4 py-2 rounded-xl text-sm font-medium flex items-center justify-center"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <span>Crear sala</span>
                      <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateRoomModal;